import React, { useEffect, useRef, useState } from 'react'
import componentList from 'componentList'
import rfdc from 'rfdc'
import shortid from 'shortid'

import { Image } from '@react/react-spectrum/Image'
import { Accordion, AccordionItem } from '@react/react-spectrum/Accordion'
import Button from '@react/react-spectrum/Button'
import Dialog from '@react/react-spectrum/Dialog'
import Heading from '@react/react-spectrum/Heading'
import ModalTrigger from '@react/react-spectrum/ModalTrigger'
import SelectList from '@react/react-spectrum/SelectList'
import { error } from '@react/react-spectrum/Toast/js/ToastContainer'

import AddToSelection from '@react/react-spectrum/Icon/AddToSelection'
import Paste from '@react/react-spectrum/Icon/Paste'

import { readValue } from 'utils/clipboard'
import { compareWithTrailingCharacters } from 'utils/collections'

let handleReorder, handleClick

const clone = rfdc()

/**
 * Render
 * @type {React.FC<{
 *  components: Array<Object>,
 *  configs: Object,
 *  selection: string,
 *  type: string,
 *  disabled: Boolean,
 *  onSelect: function(selection): any,
 *  onComponentsUpdate: function(components): any,
 * }>}
 */
const ComponentList = ({ components = [], configs, selection, type, disabled, onSelect, onComponentsUpdate }) => {
  const ref = useRef()

  const addComponent = (componentIndex, config = configs) => {
    const idx = typeof componentIndex === 'number' ? componentIndex : components.length
    const newComponents = [...components.slice(0, idx), clone(config.defaultContent), ...components.slice(idx)]
    onSelect('cmp-' + idx)
    onComponentsUpdate(newComponents)
  }

  // Initialize component list only once, when components become defined
  useEffect(() => {
    if (components && ref.current) {
      componentList.render(
        ref.current,
        clone(components),
        {
          containerClass: 'ScreenComponent__dropZone',
          draggableClass: 'ScreenComponent',
          highlightClass: 'ScreenComponent__hightlight',
          startingSelector: selection,
          disabled: disabled,
        },
        configs
      )
    }
    handleClick = (event) => {
      onSelect(event.detail)
    }
    handleReorder = (event) => onComponentsUpdate(event.detail)
    ref.current.addEventListener('componentsReordered', handleReorder)
    ref.current.addEventListener('componentClick', handleClick)

    return () => {
      ref.current.removeEventListener('componentsReordered', handleReorder)
      ref.current.removeEventListener('componentClick', handleClick)
    }
  }, [components])

  useEffect(() => {
    // ... init code if needed
    return () => {
      // cleanup component list event listeners
      componentList.cleanup()
    }
  }, [])

  return (
    <div
      className={`ComponentList ComponentList--${type}`}
      onMouseDown={(e) => {
        e.stopPropagation()
      }}
    >
      <ComponenListActions configs={configs} onAdd={(config) => addComponent(0, config)} disabled={disabled} />

      <div ref={ref} />

      <ComponenListActions configs={configs} onAdd={(config) => addComponent(null, config)} disabled={disabled} />
    </div>
  )
}

function ComponenListActions({ configs, onAdd, disabled }) {
  const onPaste = async () => {
    const defaultContent = await readValue()

    if (!defaultContent) return null

    if (defaultContent.meta?.component && defaultContent.content) {
      defaultContent.meta.id = shortid()
      onAdd({ defaultContent })
    } else {
      error('Invalid component definition. Try copying original component again.')
    }
  }

  return (
    <div className='ComponentListActions'>
      <ModalTrigger>
        <Button
          className='ComponentListActions__add'
          variant='tool'
          icon={<AddToSelection size='L' />}
          disabled={disabled}
        />
        <NewComponentDialog configs={configs} onAdd={onAdd} />
      </ModalTrigger>
      <Button
        className='ComponentListActions__paste'
        variant='tool'
        icon={<Paste size='M' />}
        onClick={onPaste}
        disabled={disabled}
      />
    </div>
  )
}

function NewComponentDialog({ configs, onAdd, ...props }) {
  let defaultContentIndex = 0
  let options = Object.entries(configs).map(([key, config], idx) => {
    if (key === 'DEFAULT_COMPONENT') {
      defaultContentIndex = idx
      return config?.meta?.component
    }

    const { name, label, description, category, thumbnail, defaultContent, priorityInListing } = config
    return { value: name, label, description, category, thumbnail, defaultContent, priorityInListing }
  })

  const defaultOption = options.splice(defaultContentIndex, 1)[0]
  const filteredOptions = options
    .filter(({ label }) => !!label)
    .sort((a, b) => compareWithTrailingCharacters(a.label, b.label))
  const [selectedValue, setSelectedValue] = useState(defaultOption)
  const selectedConfig = filteredOptions.find((c) => c.value === selectedValue)

  const categorizedOptions = filteredOptions.reduce((acc, it) => {
    const category = it?.category || 'General'
    return { ...acc, [category]: [...(acc[category] || []), it] }
  }, {})

  const sortedCategories = Object.entries(categorizedOptions)
    .sort((a, b) => {
      if (a[0] === 'General') return -1
      if (b[0] === 'General') return 1
      return compareWithTrailingCharacters(a[0], b[0])
    })
    .map(([category, components]) => [
      category,
      components.sort((a, b) => {
        // Sort by priorityInListing first (true values first)
        if (a.priorityInListing && !b.priorityInListing) return -1
        if (!a.priorityInListing && b.priorityInListing) return 1
        // Then sort alphabetically by label
        return compareWithTrailingCharacters(a.label, b.label)
      }),
    ])

  const onConfirm = () => onAdd(selectedConfig)

  return (
    <Dialog
      title='New Component'
      cancelLabel='Cancel'
      confirmLabel='Add'
      className='ComponentDialog'
      onConfirm={onConfirm}
      {...props}
      role='dialog'
    >
      <Heading size='4'>Component type</Heading>
      <Accordion>
        {sortedCategories.map(([category, components]) => (
          <AccordionItem header={category} key={category}>
            <SelectList options={components} value={selectedValue} onChange={setSelectedValue} />
          </AccordionItem>
        ))}
      </Accordion>
      {/* <Select options={filteredOptions} value={selectedValue} onChange={setSelectedValue} /> */}
      <br />
      <h3>{selectedConfig.label}</h3>
      <p className='ComponentDialog__description'>
        <em>{selectedConfig.description || 'No component description yet...'}</em>
      </p>
      {selectedConfig.thumbnail && (
        <div className='ComponentDialog__thumbnail'>
          <Image src={selectedConfig.thumbnail} />
        </div>
      )}
    </Dialog>
  )
}

export default ComponentList
